import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Waves, Users, FileText, Phone, Star, Sparkles, MessageSquare, ClipboardList, Zap, Gem, CheckCircle2, XCircle, TrendingUp, ShieldCheck, Mail } from "lucide-react";
import { api } from "@/lib/api";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

const STEPS = [
  [MessageSquare, "1 · Aggancio", "Il chatbot in home chiede solo nome e cognome e porta alla scheda di valutazione: zero attrito."],
  [ClipboardList, "2 · Scheda guidata", "Form da ~2 minuti con autocompletamento dell'indirizzo, barra di completamento e correzioni suggerite a un click."],
  [Zap, "3 · Valutazione istantanea", "Range di valore basato sui dati OMI ufficiali dell'Agenzia delle Entrate + premi e malus del singolo immobile."],
  [Gem, "4 · Premium a pagamento", "Brochure di 11 pagine, pagamento PayPal, fattura, call di 30 minuti e follow-up automatici."],
];

const COMPARE = [
  ["Range di valore OMI (min–max)", true, true],
  ["Dossier PDF via email", true, true],
  ["Analisi 22 fattori Premium con valore in €", false, true],
  ["Grafico quotazioni OMI di zona", false, true],
  ["Valore preciso con metodo MCM completo", false, true],
  ["Strategia di vendita (prezzo uscita, obiettivo, minimo)", false, true],
  ["Brochure professionale 11 pagine", false, true],
  ["Call di 30 minuti con consulente Gruppo Vela", false, true],
  ["Visura catastale richiesta per te (+€5)", false, true],
  ["Fattura con dati fiscali", false, true],
];

export default function Demo() {
  const [stats, setStats] = useState(null);

  useEffect(() => {
    document.title = "ENEA Specialist — Demo per il team";
    api.get("/demo-stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);

  const nums = [
    [Users, "Richieste di valutazione", stats?.total_leads, "demo-total"],
    [FileText, "Valutazioni Premium richieste", stats?.certificata, "demo-cert"],
    [Phone, "Richieste di call", stats?.call_requests, "demo-calls"],
    [Gem, "Premium vendute", stats?.premium_sold, "demo-sold"],
  ];

  return (
    <div className="min-h-screen bg-[#081c33] text-white" data-testid="demo-page">
      {/* Hero */}
      <div className="relative overflow-hidden">
        <div className="absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full bg-[#14B8A6]/10 blur-3xl pointer-events-none" />
        <div className="max-w-6xl mx-auto px-6 lg:px-10 pt-14 pb-10">
          <div className="flex items-center gap-3">
            <span className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A9D6E5] to-[#14B8A6] flex items-center justify-center">
              <Waves className="w-6 h-6 text-[#0b2545]" />
            </span>
            <div>
              <div className="text-sm font-semibold">Home Exchange Copilot AI · Gruppo Vela</div>
              <div className="text-[11px] text-[#A9D6E5] uppercase tracking-[0.22em]">Presentazione ufficiale al team</div>
            </div>
          </div>
          <h1 className="mt-8 text-4xl sm:text-5xl lg:text-6xl font-display font-semibold leading-tight">
            ENEA <span className="text-[#14B8A6]">Specialist</span>
          </h1>
          <p className="mt-4 max-w-2xl text-[#A9D6E5]/90 text-base md:text-lg">
            La macchina che trasforma un proprietario curioso in un cliente pagante:
            valutazione immobiliare istantanea su dati OMI ufficiali, monetizzata con la Premium a pagamento.
          </p>
          {stats?.launch_remaining > 0 && (
            <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-amber-500/15 border border-amber-400/50 px-4 py-2 text-sm font-bold text-amber-300" data-testid="demo-launch-badge">
              🔥 Offerta lancio LIVE: rimasti {stats.launch_remaining} posti su {stats.launch_quota} a €{stats.launch_price_eur} (poi €{stats.standard_price_eur})
            </div>
          )}
        </div>
      </div>

      {/* Numeri chiave */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 pb-4">
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="demo-stats-grid">
          {nums.map(([Icon, label, value, tid]) => (
            <div key={label} className="bg-[#0b2545] border border-white/10 rounded-2xl p-6" data-testid={tid}>
              <Icon className="w-5 h-5 text-[#14B8A6]" />
              <div className="mt-3 text-4xl lg:text-5xl font-display font-semibold text-white">{value ?? "—"}</div>
              <div className="mt-1 text-xs text-[#A9D6E5]/80 uppercase tracking-wider">{label}</div>
            </div>
          ))}
        </div>
        {stats?.reviews_count > 0 && (
          <div className="mt-4 flex items-center gap-2 text-sm text-[#A9D6E5]" data-testid="demo-reviews">
            <Star className="w-4 h-4 fill-amber-400 text-amber-400" />
            <b className="text-white">{String(stats.reviews_avg).replace(".", ",")}/5</b> · {stats.reviews_count} recension{stats.reviews_count === 1 ? "e" : "i"} verificat{stats.reviews_count === 1 ? "a" : "e"} dai clienti Premium
          </div>
        )}
      </div>

      {/* Percorso */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#14B8A6]">Il funnel</div>
        <h2 className="mt-2 text-lg md:text-lg font-display font-semibold">Dal primo click all'incasso, in automatico</h2>
        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {STEPS.map(([Icon, title, desc]) => (
            <div key={title} className="bg-[#0b2545] border border-white/10 rounded-2xl p-6" data-testid="demo-step">
              <span className="w-9 h-9 rounded-lg bg-[#14B8A6]/15 border border-[#14B8A6]/30 flex items-center justify-center">
                <Icon className="w-4 h-4 text-[#14B8A6]" />
              </span>
              <div className="mt-3 text-sm font-bold">{title}</div>
              <p className="mt-1.5 text-xs text-[#A9D6E5]/80 leading-relaxed">{desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-3 text-[11px] text-[#A9D6E5]/70">
          <span className="inline-flex items-center gap-1.5"><ShieldCheck className="w-3.5 h-3.5 text-[#14B8A6]" /> Pagamenti PayPal Live</span>
          <span className="inline-flex items-center gap-1.5"><Mail className="w-3.5 h-3.5 text-[#14B8A6]" /> Email automatiche: brochure, promemoria carrello, recensione (3gg), call (5gg)</span>
          <span className="inline-flex items-center gap-1.5"><TrendingUp className="w-3.5 h-3.5 text-[#14B8A6]" /> Notifiche Telegram in tempo reale al team</span>
        </div>
      </div>

      {/* Base vs Premium */}
      <div className="bg-[#0b2545]/60 border-y border-white/10">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
          <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#14B8A6]">Il modello di business</div>
          <h2 className="mt-2 text-lg md:text-lg font-display font-semibold">Base gratuita che aggancia, Premium che incassa</h2>
          <div className="mt-8 overflow-x-auto rounded-2xl border border-white/10" data-testid="demo-compare-table">
            <table className="w-full text-sm min-w-[560px]">
              <thead>
                <tr className="bg-[#081c33] text-left">
                  <th className="px-5 py-4 text-[11px] uppercase tracking-wider text-slate-400 w-1/2">Cosa riceve il cliente</th>
                  <th className="px-5 py-4 text-center">
                    <div className="text-sm font-bold">Base</div>
                    <div className="text-[11px] text-[#14B8A6] font-bold">GRATUITA</div>
                  </th>
                  <th className="px-5 py-4 text-center bg-[#14B8A6]/10">
                    <div className="text-sm font-bold">💎 Premium</div>
                    <div className="text-[11px] text-amber-300 font-bold">
                      €{stats?.launch_remaining > 0 ? stats.launch_price_eur : (stats?.standard_price_eur ?? 279)}
                      {stats?.launch_remaining > 0 && <span className="text-slate-400 line-through ml-1">€{stats.standard_price_eur}</span>}
                    </div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {COMPARE.map(([label, base, prem]) => (
                  <tr key={label} className="border-t border-white/5">
                    <td className="px-5 py-3 text-[#A9D6E5]/90 text-xs md:text-sm">{label}</td>
                    <td className="px-5 py-3 text-center">
                      {base ? <CheckCircle2 className="w-4 h-4 text-[#14B8A6] inline" /> : <XCircle className="w-4 h-4 text-slate-600 inline" />}
                    </td>
                    <td className="px-5 py-3 text-center bg-[#14B8A6]/5">
                      {prem ? <CheckCircle2 className="w-4 h-4 text-[#14B8A6] inline" /> : <XCircle className="w-4 h-4 text-slate-600 inline" />}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Slide visiva: stesso immobile, due esperienze */}
          <div className="mt-12" data-testid="demo-visual-compare">
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#14B8A6]">La slide del confronto</div>
            <h3 className="mt-2 text-lg font-display font-semibold">Stesso immobile, due esperienze — Appartamento a Este (PD), 120 mq, 2° piano, ascensore + garage</h3>
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6 items-stretch">

              {/* Card BASE */}
              <div className="rounded-2xl bg-white text-[#0b2545] overflow-hidden shadow-2xl" data-testid="demo-card-base">
                <div className="px-5 py-3 bg-slate-100 border-b border-slate-200 flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider">Valutazione Base</span>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-[#14B8A6]/15 text-[#0d9488]">GRATUITA</span>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Valore stimato</div>
                  <div className="mt-1 text-2xl lg:text-3xl font-display font-semibold">€ 144.900 – € 189.000</div>
                  <div className="mt-1 text-[11px] text-slate-500">Range OMI ufficiale min–max della zona</div>
                  <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500">Premi / malus applicati</div>
                  <div className="mt-2 space-y-1.5 text-sm">
                    <div className="flex justify-between"><span className="text-[#0d9488]">Ascensore</span><span className="font-semibold">+3% · +€ 4.770</span></div>
                    <div className="flex justify-between"><span className="text-[#0d9488]">Garage / posto auto</span><span className="font-semibold">+2% · +€ 3.180</span></div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" /> Report PDF scaricabile subito, gratis
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" /> Dossier inviato automaticamente al team
                  </div>
                </div>
              </div>

              {/* Card PREMIUM */}
              <div className="rounded-2xl bg-white text-[#0b2545] overflow-hidden shadow-2xl ring-2 ring-[#14B8A6]" data-testid="demo-card-premium">
                <div className="px-5 py-3 bg-[#0b2545] flex items-center justify-between">
                  <span className="text-xs font-bold uppercase tracking-wider text-white">💎 Valutazione Premium</span>
                  <span className="text-[10px] font-bold px-2.5 py-1 rounded-full bg-amber-400 text-[#0b2545]">
                    €{stats?.launch_remaining > 0 ? stats.launch_price_eur : (stats?.standard_price_eur ?? 279)}
                  </span>
                </div>
                <div className="p-5">
                  <div className="text-[10px] uppercase tracking-wider text-slate-500">Valore stimato parziale (anteprima)</div>
                  <div className="mt-1 text-2xl lg:text-3xl font-display font-semibold">€ 162.840 – € 212.400</div>
                  <div className="mt-1 text-[11px] font-semibold text-[#0d9488]">🔒 Il valore preciso si sblocca con la brochure</div>
                  <div className="mt-4 text-[10px] uppercase tracking-wider text-slate-500">I 22 fattori Premium (fino a +40%)</div>
                  <div className="relative mt-2">
                    <div className="space-y-1.5 text-sm blur-[5px] select-none" aria-hidden="true">
                      <div className="flex justify-between"><span className="text-[#0d9488]">Classe energetica C</span><span className="font-semibold">+1% · +€ 1.880</span></div>
                      <div className="flex justify-between"><span className="text-[#0d9488]">Riscaldamento autonomo</span><span className="font-semibold">+2% · +€ 3.760</span></div>
                      <div className="flex justify-between"><span className="text-[#0d9488]">Vista panoramica</span><span className="font-semibold">+3% · +€ 5.640</span></div>
                      <div className="flex justify-between"><span className="text-[#0d9488]">Doppi servizi</span><span className="font-semibold">+2% · +€ 3.760</span></div>
                    </div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="bg-white border border-slate-200 rounded-full px-4 py-1.5 text-[11px] font-bold shadow">🔒 Si sbloccano dopo il pagamento</span>
                    </div>
                  </div>
                  <div className="mt-4 flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" /> Brochure 11 pagine via email + fattura
                  </div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs text-slate-600">
                    <CheckCircle2 className="w-4 h-4 text-[#14B8A6]" /> Call di 30 minuti con consulente Gruppo Vela
                  </div>
                </div>
              </div>
            </div>
            <p className="mt-4 text-[11px] text-[#A9D6E5]/70">
              Numeri reali del motore MCM sullo stesso immobile: la Premium analizza i 22 fattori (qui APE C, riscaldamento autonomo, clima, infissi doppi, esposizione, vista panoramica, doppi servizi, cantina, ristrutturazione 2021) e mostra il range affinato — ma i dettagli in euro restano bloccati finché il cliente non paga. È il momento in cui capisce cosa sta comprando.
            </p>
          </div>
        </div>
      </div>

      {/* QR + CTA */}
      <div className="max-w-6xl mx-auto px-6 lg:px-10 py-14">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          <div>
            <div className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#14B8A6]">Provalo adesso</div>
            <h2 className="mt-2 text-lg md:text-lg font-display font-semibold">Inquadra il QR e fai una valutazione dal tuo telefono</h2>
            <p className="mt-3 text-sm text-[#A9D6E5]/80 max-w-md leading-relaxed">
              Il QR porta direttamente alla scheda di valutazione di eneaspecialist.it — lo stesso che useremo su cartoline, vetrine e social.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/valutazione" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg bg-[#14B8A6] text-[#06202b] text-sm font-bold hover:bg-[#2dd4bf] transition" data-testid="demo-cta-valutazione">
                <Sparkles className="w-4 h-4" /> Apri la valutazione
              </Link>
              <Link to="/" className="inline-flex items-center gap-2 px-6 py-3 rounded-lg border border-white/25 text-white/85 text-sm font-medium hover:border-[#14B8A6] transition" data-testid="demo-cta-home">
                Vai alla home
              </Link>
            </div>
          </div>
          <div className="flex justify-center lg:justify-end">
            <div className="bg-white rounded-3xl p-6 shadow-2xl" data-testid="demo-qr">
              <img src={`${BACKEND}/api/qr-valutazione`} alt="QR valutazione eneaspecialist.it" className="w-56 h-56 object-contain" />
              <div className="mt-2 text-center text-[11px] font-bold text-[#0b2545]">eneaspecialist.it/valutazione</div>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-white/10 py-6 text-center text-[11px] text-[#A9D6E5]/60">
        Gruppo Vela — Vele Cinque S.r.l. · Dati aggiornati in tempo reale · eneaspecialist.it
      </div>
    </div>
  );
}
